import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { History, Filter, RefreshCcw } from "lucide-react";
import PageHeader from "../../components/PageHeader";

const COLECCIONES = [
  "todos", "clientes", "cotizaciones", "contratos", "eventos", "pagos",
  "egresos", "inventario_items", "proveedores", "personal", "configuracion_empresa"
];

const Auditoria = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [coleccion, setColeccion] = useState("todos");
  const [accion, setAccion] = useState("todos");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (coleccion !== "todos") params.coleccion = coleccion;
      if (accion !== "todos") params.accion = accion;
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      const res = await axios.get(`${API}/audit-log`, { params });
      setLogs(res.data.logs || []);
    } catch (e) {
      toast.error("No se pudo cargar la auditoría");
    } finally {
      setLoading(false);
    }
  }, [coleccion, accion, desde, hasta]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const colorByAccion = (a) => {
    if (a === "creacion") return "bg-emerald-100 text-emerald-800";
    if (a === "edicion") return "bg-blue-100 text-blue-800";
    if (a === "cancelacion") return "bg-red-100 text-red-700";
    if (a === "registrar_abono") return "bg-amber-100 text-amber-800";
    if (a === "firma") return "bg-purple-100 text-purple-800";
    if (a === "role_change") return "bg-orange-100 text-orange-800";
    return "bg-stone-100 text-stone-700";
  };

  const accionesUnicas = Array.from(new Set(logs.map(l => l.accion))).sort();

  return (
    <div className="space-y-6">
      <PageHeader
        icon={History}
        title="Auditoría global"
        subtitle="Registro completo de cambios en todos los módulos"
        actions={
          <Button onClick={fetchLogs} variant="outline" className="border-stone-300" data-testid="btn-refresh-auditoria">
            <RefreshCcw className="w-4 h-4 mr-2" /> Actualizar
          </Button>
        }
      />

      {/* Filtros */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Select value={coleccion} onValueChange={setColeccion}>
          <SelectTrigger className="border-stone-300" data-testid="filter-coleccion-auditoria">
            <Filter className="w-4 h-4 mr-2" /><SelectValue placeholder="Módulo" />
          </SelectTrigger>
          <SelectContent>
            {COLECCIONES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={accion} onValueChange={setAccion}>
          <SelectTrigger className="border-stone-300" data-testid="filter-accion-auditoria">
            <SelectValue placeholder="Acción" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todas las acciones</SelectItem>
            {accionesUnicas.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="border-stone-300" data-testid="filter-desde-auditoria" />
        <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="border-stone-300" data-testid="filter-hasta-auditoria" />
      </div>

      {/* Lista */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : logs.length === 0 ? (
        <Card className="border-stone-200"><CardContent className="p-12 text-center">
          <History className="w-10 h-10 text-stone-300 mx-auto mb-3" />
          <h3 className="font-display text-xl text-stone-900 mb-2">Sin movimientos</h3>
          <p className="text-stone-500 text-sm">No hay actividad registrada con los filtros actuales.</p>
        </CardContent></Card>
      ) : (
        <Card className="border-stone-200">
          <CardContent className="p-0">
            <div className="divide-y divide-stone-100">
              {logs.map((log) => (
                <div key={log.log_id || (log.fecha + log.coleccion + log.doc_id)} className="p-4 hover:bg-stone-50" data-testid={`audit-row-${log.log_id || log.fecha}`}>
                  <div className="flex items-start gap-3 flex-wrap">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${colorByAccion(log.accion)}`}>
                      {log.accion}
                    </span>
                    <span className="text-xs text-stone-500">{new Date(log.fecha).toLocaleString("es-MX")}</span>
                    <span className="text-xs text-stone-600 font-medium">{log.coleccion}</span>
                    <span className="text-xs text-stone-400">·</span>
                    <span className="text-xs text-stone-500 font-mono">{log.doc_id || log.user_id_afectado || "—"}</span>
                  </div>
                  <p className="text-sm text-stone-800 mt-1">{log.descripcion || "(sin descripción)"}</p>
                  <p className="text-xs text-stone-500 mt-0.5">por {log.usuario_nombre || log.usuario_email || log.ejecutado_por || "sistema"}</p>
                  {log.cambios && Object.keys(log.cambios).length > 0 && (
                    <div className="mt-2 text-[11px] space-y-0.5 bg-stone-50 p-2 rounded">
                      {Object.entries(log.cambios).slice(0, 8).map(([campo, val]) => (
                        <div key={campo} className="text-stone-600">
                          <span className="font-medium">{campo}:</span>{" "}
                          <span className="line-through text-red-500">{val?.antes ? String(val.antes).substring(0, 60) : "—"}</span>
                          {" → "}
                          <span className="text-emerald-700">{val?.despues ? String(val.despues).substring(0, 60) : "—"}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Auditoria;
